import { useEffect, useState } from 'react'
import { Button } from '@unified-lk/ui'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../api/staff'
import { formatDateTime, formatStatusLabel } from '../../lib/format'
import type { PublicGiftCertificateData, PublicGiftCertificateFailure } from '../../types/plans'

export function PublicGiftCertificatePage({
  bootstrap,
  publicToken,
}: {
  bootstrap: TenantBootstrapModel
  publicToken: string
}) {
  const [certificate, setCertificate] = useState<PublicGiftCertificateData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<PublicGiftCertificateFailure | null>(null)

  useEffect(() => {
    let isMounted = true

    setLoading(true)
    setError(null)

    void staffApi.getPublicGiftCertificate(bootstrap, publicToken)
      .then((payload) => {
        if (isMounted) {
          setCertificate(payload)
        }
      })
      .catch((reason: Error & { details?: PublicGiftCertificateFailure }) => {
        if (isMounted) {
          setError(reason.details ?? {
            status: 'not_found',
            error: reason.message || 'Подарочный сертификат недоступен.',
          })
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [bootstrap, publicToken])

  const accentColor = certificate?.theme.primary || bootstrap.branding.primaryColor || '#c6ac4a'
  const backgroundStyle = certificate?.theme.backgroundImageUrl
    ? `linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.96)), url("${certificate.theme.backgroundImageUrl}")`
    : `linear-gradient(135deg, color-mix(in srgb, ${accentColor} 18%, white), white)`

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(198,172,74,0.12),transparent_28%),linear-gradient(180deg,#fafafa_0%,#f5f6f8_100%)] px-4 py-6 text-slate-900 sm:px-6"
      style={{ ['--public-accent' as string]: accentColor }}
    >
      <div className="mx-auto grid max-w-[1200px] gap-5">
        <header className="flex flex-col gap-4 rounded-[28px] border border-slate-200/70 bg-white/95 p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)] md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[var(--public-accent)]">{bootstrap.branding.appName}</p>
            <h1 className="mt-2 text-3xl font-semibold text-slate-900">Gift certificate</h1>
            <p className="mt-1 text-sm leading-6 text-slate-500">Публичная витрина сертификата в unified staff frontend.</p>
          </div>
          <Button type="button" onClick={() => window.print()}>Печать</Button>
        </header>

        {loading ? (
          <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 text-sm leading-6 text-slate-500 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <p>Загружаем подарочный сертификат...</p>
          </section>
        ) : error ? (
          <section className="rounded-[28px] border border-rose-200 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <h1 className="text-2xl font-semibold text-slate-900">{formatStatusLabel(error.status)}</h1>
            <p className="mt-2 text-sm leading-6 text-rose-700">{error.error}</p>
          </section>
        ) : certificate ? (
          <main className="grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_340px]">
            <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
              <div className="grid gap-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[var(--public-accent)]">Подарочный сертификат</p>
                  <h1 className="mt-2 text-4xl font-semibold text-slate-900">{certificate.title}</h1>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    Персональный сертификат сформирован клиникой и сохранен в shared staff platform.
                  </p>
                </div>

                <div
                  className="grid gap-4 rounded-[28px] border border-slate-200/70 px-6 py-8 shadow-sm"
                  style={{
                    ['--certificate-primary' as string]: certificate.theme.primary ?? bootstrap.branding.primaryColor ?? '#0b74de',
                    backgroundImage: backgroundStyle,
                  }}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[var(--certificate-primary)]">{certificate.brand_name}</p>
                  <h3 className="text-2xl font-semibold text-slate-900">{certificate.title}</h3>
                  <strong className="text-lg text-slate-900">{certificate.recipient_name}</strong>
                  <strong className="text-xl text-slate-900">{certificate.amount_label}</strong>
                  <p className="text-sm leading-6 text-slate-600">{certificate.offer_text}</p>
                  <p className="text-sm leading-6 text-slate-500">{certificate.support_text}</p>
                </div>
              </div>
            </section>

            <aside className="grid gap-5">
              <section className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
                <h2 className="text-xl font-semibold text-slate-900">Детали</h2>
                <ul className="mt-4 grid gap-3 p-0">
                  <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><span className="block text-sm text-slate-500">Статус</span><strong className="text-slate-900">{formatStatusLabel(certificate.status)}</strong></li>
                  <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><span className="block text-sm text-slate-500">Сумма / скидка</span><strong className="text-slate-900">{certificate.amount_label}</strong></li>
                  <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><span className="block text-sm text-slate-500">Выпущен</span><strong className="text-slate-900">{formatDateTime(certificate.issued_at)}</strong></li>
                  <li className="list-none rounded-[20px] border border-slate-200/70 bg-slate-50 px-4 py-4"><span className="block text-sm text-slate-500">Действует до</span><strong className="text-slate-900">{formatDateTime(certificate.expires_at)}</strong></li>
                </ul>
              </section>
            </aside>
          </main>
        ) : null}
      </div>
    </div>
  )
}
